import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Ticket, Copy, Trash2, Check } from "lucide-react";

const badge = {
  available: { label: "Disponible", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  used: { label: "Canjeado", cls: "bg-white/5 text-muted-foreground border-white/10" },
  expired: { label: "Caducado", cls: "bg-crimson/15 text-crimson border-crimson/30" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("es", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function MembershipCodes({ groups }) {
  const [form, setForm] = useState({ days: 30, quantity: 1, redeemDays: 90 });
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [pick, setPick] = useState({});
  const inputCls = "w-full bg-obsidian border border-white/10 rounded-md text-white px-3 py-2.5 text-sm focus:border-gold focus:ring-1 focus:ring-gold outline-none";

  const load = useCallback(async () => {
    try { setCodes(await api.listCodes()); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { days: Number(form.days), quantity: Number(form.quantity) };
      if (form.redeemDays && Number(form.redeemDays) > 0) body.redeemDays = Number(form.redeemDays);
      const created = await api.generateCodes(body);
      toast.success(`${created.length} código(s) generado(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar");
    } finally { setBusy(false); }
  };

  const copy = (code) => {
    const text = `!redeem ${code}`;
    const done = () => {
      setCopied(code);
      toast.success(`Copiado: ${text}`);
      setTimeout(() => setCopied(""), 1500);
    };
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
      } else {
        fallbackCopy(text, done);
      }
    } catch (e) {
      fallbackCopy(text, done);
    }
  };

  const fallbackCopy = (text, done) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      done();
    } catch (e) {
      toast.error("No se pudo copiar. Copia manualmente: " + text);
    }
  };

  const redeem = async (code) => {
    const chatId = pick[code];
    if (!chatId) { toast.error("Elige un grupo para canjear"); return; }
    try {
      const g = await api.redeemCode(code, chatId);
      toast.success(`Canjeado en ${g.name || g.clanTag}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al canjear");
    }
  };

  const del = async (code, used) => {
    if (used && !window.confirm(`¿Eliminar el código ${code}? Ya fue canjeado; esto solo borra el registro (no revoca la membresía del grupo).`)) return;
    try { await api.deleteCode(code); toast.success("Código eliminado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const eligibleGroups = groups.filter((g) => g.clanTag && g.licenseState?.everActivated);
  const available = codes.filter((c) => c.status === "available").length;

  return (
    <div className="space-y-4">
      <div className="card-surface p-6" data-testid="generate-codes-card">
        <div className="flex items-center gap-2 mb-1">
          <Ticket className="text-gold" size={18} />
          <h3 className="font-heading font-semibold">Generar códigos de membresía</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Códigos de un solo uso para <b>renovar</b> grupos ya activados. El cliente los canjea con <code className="text-gold">!redeem CÓDIGO</code> en su grupo.
        </p>
        <form onSubmit={generate} className="grid md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="overline block mb-1.5">Días del plan</label>
            <input data-testid="gen-days" type="number" min="1" max="3650" className={inputCls} value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} />
          </div>
          <div>
            <label className="overline block mb-1.5">Cantidad</label>
            <input data-testid="gen-quantity" type="number" min="1" max="100" className={inputCls} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
          </div>
          <div>
            <label className="overline block mb-1.5">Caduca en (días)</label>
            <input data-testid="gen-redeemdays" type="number" min="0" max="3650" placeholder="0 = sin caducidad" className={inputCls} value={form.redeemDays} onChange={(e) => setForm({ ...form, redeemDays: e.target.value })} />
          </div>
          <div>
            <button data-testid="gen-submit" disabled={busy} className="w-full bg-gold text-black font-bold rounded-md px-6 py-2.5 text-sm hover:brightness-95 transition-all disabled:opacity-60">
              {busy ? "Generando…" : "Generar"}
            </button>
          </div>
        </form>
      </div>

      <div className="card-surface overflow-hidden" data-testid="codes-table">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <span className="overline">Códigos ({available} disponibles / {codes.length} total)</span>
        </div>
        {codes.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Aún no has generado códigos.</div>
        ) : (
          <div className="divide-y divide-white/5 max-h-[480px] overflow-y-auto">
            {codes.map((c) => {
              const b = badge[c.status] || badge.available;
              return (
                <div key={c.code} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3.5 hover:bg-white/[0.02]" data-testid={`code-row-${c.code}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <button data-testid={`code-copy-${c.code}`} onClick={() => copy(c.code)} title="Copiar !redeem" className="shrink-0 text-gold hover:bg-gold/10 rounded-md p-1.5 transition-all">
                      {copied === c.code ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-semibold truncate">!redeem {c.code}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {c.days}d plan · {c.redeemBy ? `canje hasta ${fmt(c.redeemBy)}` : "sin caducidad"}
                        {c.used && c.usedByName ? ` · usado por ${c.usedByName}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${b.cls}`}>{b.label}</span>
                    {c.status === "available" && (
                      <>
                        <select
                          data-testid={`code-group-select-${c.code}`}
                          className="bg-obsidian border border-white/10 rounded-md text-white px-2 py-1.5 text-xs max-w-[160px] focus:border-gold outline-none"
                          value={pick[c.code] || ""}
                          onChange={(e) => setPick({ ...pick, [c.code]: e.target.value })}
                        >
                          <option value="">Canjear en…</option>
                          {eligibleGroups.map((g) => (
                            <option key={g.chatId} value={g.chatId}>{g.name || g.clanTag}</option>
                          ))}
                        </select>
                        <button data-testid={`code-redeem-${c.code}`} onClick={() => redeem(c.code)} className="text-xs bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-md px-2.5 py-1.5 hover:bg-emerald-500/25 transition-all">
                          Canjear
                        </button>
                      </>
                    )}
                    <button data-testid={`code-delete-${c.code}`} onClick={() => del(c.code, c.used)} title="Eliminar código" className="text-crimson hover:bg-crimson/10 rounded-md p-1.5 transition-all">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
